import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserDto } from '../models/dto';
import { AppConfigService } from './app-config.service';
import { formatDatetimeShorterLabel } from '../infra/appHelper';
import moment from 'moment';
import { DeviceDetectorService } from 'ngx-device-detector';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public  static ROLE_SYSTEM_ADMIN = 'SuperAdmin';

  loggedIn = true;
  private baseUrl = `${this.appConfig.CoreAPIUrl}/api/auth`;
  private loginUrl = `${this.baseUrl}/authenticate`;
  private refreshTokenUrl = `${this.appConfig.CoreAPIUrl}/api/auth/token/refresh`;
  private currentUserSubject: BehaviorSubject<UserDto>;
  private currentClientPlatform: string;
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    public appConfig: AppConfigService,
    private deviceService: DeviceDetectorService) {

      const isMobile = this.deviceService.isMobile();

    const savedSession: UserDto = this.loadUserSessionFromStorage();
    // console.log('AuthService load last savedSession', savedSession);
    this.currentUserSubject = new BehaviorSubject<any>(savedSession);

    if (savedSession && savedSession.clientPlatform) {
       // console.log('AuthService restoring clientPlatform', savedSession.clientPlatform);
       this.currentClientPlatform = savedSession.clientPlatform;
    }

    const isDesktopDevice = this.deviceService.isDesktop();
    if (!this.currentClientPlatform) {
      this.currentClientPlatform = isDesktopDevice ? 'W' : 'MW';
    }
  }

  private loadUserSessionFromStorage(): UserDto {
    const sessionDetails: UserDto = JSON.parse(localStorage.getItem('currentUser'));
    return sessionDetails;
  }

  public reloadUserSession() {
    const sessionDetails: UserDto = this.loadUserSessionFromStorage();
    this.currentUserSubject.next(sessionDetails);
    console.log('reloadUserSession=>', JSON.stringify(sessionDetails));
  }

  public get currentUserValue(): UserDto {
    return this.currentUserSubject.value;
  }

  public getClientPlatform(): string {
    return this.currentClientPlatform;
  }

  public get userRoles(): string[] {
    return this.currentUserValue && this.currentUserValue.customer ? this.currentUserValue.customer.roles : null;
  }

  public get currentUserId(): number {
    return this.currentUserValue && this.currentUserValue.customer ? this.currentUserValue.customer.userId : null;
  }

  public get currentUserEmail(): string {
    return this.currentUserValue && this.currentUserValue.customer ? this.currentUserValue.customer.email : null;
  }

  login(username: string, password: string) {
    // return this.http.post<any>(this.loginUrl, { username, password, secondAfter: 10 }, this.httpOptions).pipe(
    return this.http.post<any>(this.loginUrl, { username, password }, this.httpOptions).pipe(
      map(resp => {
        const sessionDetails = resp.data;
        if (sessionDetails) {
          console.log(`resp.data >> ${JSON.stringify(sessionDetails)}`);
          // console.log(`userObj >> ${JSON.stringify(userObj)}`);
          this.setSession(sessionDetails);
          this.currentUserSubject.next(sessionDetails);
        }

        return sessionDetails;
      }));
    // uncomment for testing
    // const sessionDetails: any = {
    //     customer: {
    //       name: 'Js Yau',
    //       email: 'jsyau@gmail.com'
    //     },
    //     accessToken: '123',
    //     refreshToken: '123'
    // };
    // this.setSession(sessionDetails);
    // this.currentUserSubject.next(sessionDetails);
  }

  registerUser(payload) {
    return this.http.post<any>(`${this.baseUrl}/register/otp`, payload, this.httpOptions);
  }

  registerUserWithOtp(payload) {
    return this.http.post<any>(`${this.baseUrl}/register`, payload, this.httpOptions);
  }

  resetPassword(payload) {
    return this.http.post<any>(`${this.baseUrl}/reset-password/otp`, payload, this.httpOptions);
  }

  verifyResetPasswordOtp(payload) {
    return this.http.post<any>(`${this.baseUrl}/reset-password/otp/validate`, payload, this.httpOptions);
  }

  resetPasswordWithOtp(payload) {
    return this.http.post<any>(`${this.baseUrl}/reset-password`, payload, this.httpOptions);
  }

  recoverAccount(payload) {
    return this.http.post<any>(`${this.baseUrl}/recover-username/otp`, payload, this.httpOptions);
  }

  recoverAccountWithOtp(payload) {
    return this.http.post<any>(`${this.baseUrl}/recover-username`, payload, this.httpOptions);
  }

  unlockAccount(payload) {
    return this.http.post<any>(`${this.baseUrl}/unlock/otp`, payload, this.httpOptions);
  }

  unlockAccountWithOtp(payload) {
    return this.http.post<any>(`${this.baseUrl}/unlock`, payload, this.httpOptions);
  }

  refreshToken(refreshToken: string) {
    // Remove existing tokenDetails
    // console.log('refreshToken. Delete local session',  refreshToken, this.currentClientPlatform);
    this.clearSession();

    // return this.http.post<any>(this.refreshTokenUrl, { refreshToken, secondAfter: 30 }, this.httpOptions).pipe(
    return this.http.post<any>(this.refreshTokenUrl, { refreshToken }, this.httpOptions).pipe(
      map(resp => {
        const sessionDetails: UserDto = resp.data;
        if (sessionDetails) {
          console.log(`Renew refresh token successful >> ${JSON.stringify(resp)}`);
          this.setSession(sessionDetails);
          this.currentClientPlatform = sessionDetails.clientPlatform;
          this.currentUserSubject.next(sessionDetails);
        } else {
          console.log(`Renew refresh token failed >> ${JSON.stringify(resp)}`);
        }
        return sessionDetails;
      }));
  }

  logout() {
    this.clearSession();
    this.router.navigate([this.appConfig.LoginUrl]);
  }

  resendActivationCode(email: string) {
    return this.http.post<any>(`${this.baseUrl}/activate/resend`, { username: email }, this.httpOptions);
  }

  activateAccount(code: string) {
    return this.http.post<any>(`${this.baseUrl}/activate`, { ac: code }, this.httpOptions);
  }

  public getJwtToken(): string {
    return this.currentUserValue ? this.currentUserValue.accessToken : null;
  }

  public getRefreshToken(): string {
    // Always get the refreshtoken from localstorage as it could have been updated by diff browser tab
    const savedSession: UserDto = this.loadUserSessionFromStorage();
    return savedSession ? savedSession.refreshToken : null;
  }

  private clearSession() {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }

  private setSession(authResult) {
    // console.log(`setSession save session to storage >> ${JSON.stringify(authResult)}`);
    localStorage.setItem('currentUser', JSON.stringify(authResult));
  }
}
